/**
 * CareLogix™ ORACLE — Phase 36 · Global Intelligence Globe™ (3D)
 *
 * Real interactive 3D globe via `react-globe.gl` (three.js under the hood).
 * Constitutional invariants preserved:
 *   - Consumes existing endpoints only.
 *   - No simulated opportunities / no fabricated regional dots.
 *   - Empty layers surface `NO_DATA_AVAILABLE`.
 *   - Truthful disclosure: this is a visualization on top of real data,
 *     not predictive intelligence.
 */
import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

// react-globe.gl is heavy — lazy load so the rest of the app stays fast.
const Globe = lazy(() => import("react-globe.gl"));

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  "";

// Coarse region centroids (lat, lng). We intentionally avoid implying precision
// we do not have — each dot represents a region, not a specific facility.
const REGION_GEO = {
  "US":              { lat: 39.5,  lng: -98.0,   label: "United States" },
  "United States":   { lat: 39.5,  lng: -98.0,   label: "United States" },
  "USA":             { lat: 39.5,  lng: -98.0,   label: "United States" },
  "Africa":          { lat: 1.5,   lng: 21.0,    label: "Africa" },
  "Africa/Global":   { lat: 1.5,   lng: 21.0,    label: "Africa" },
  "UK":              { lat: 54.0,  lng: -2.0,    label: "United Kingdom" },
  "UK/Global":       { lat: 54.0,  lng: -2.0,    label: "United Kingdom" },
  "Global":          { lat: 20.0,  lng: 0.0,     label: "Global" },
  "Diaspora":        { lat: 30.0,  lng: -40.0,   label: "Diaspora" },
};

const LAYER_DEFS = [
  { key: "services",      label: "CareLogix Coverage",          color: "#7AC860" },
  { key: "foundations",   label: "Foundations Registry",        color: "#c5a059" },
  { key: "jurisdictions", label: "Jurisdiction Policies",       color: "#60B8E8" },
  { key: "audit",         label: "Compliance Signals (24h)",    color: "#E5484D" },
  { key: "events",        label: "World Mesh Events (Phase 35)", color: "#a78bfa" },
];

// Phase 35 — color-coded pin palette per event type.
const EVENT_COLOR = {
  GRANT:        "#7AC860", // green — opportunity
  CONTRACT:     "#7AC860",
  DONOR:        "#7AC860",
  FOUNDATION:   "#7AC860",
  INVESTMENT:   "#c5a059", // gold — funding
  IPO:          "#c5a059",
  HEALTHCARE:   "#60B8E8", // blue — healthcare
  BIOTECH:      "#60B8E8",
  PHARMA:       "#60B8E8",
  PUBLIC_HEALTH:"#60B8E8",
  REGULATORY:   "#a78bfa", // purple — regulatory
  GOVERNMENT:   "#a78bfa",
  GEOPOLITICAL: "#E5484D", // red — risk
  COMPETITOR:   "#E5484D",
  AI_COMPANY:   "#c5a059",
  TECHNOLOGY:   "#94a3b8",
};

// Lightweight geo binder — uses event text hints, defaults to "Global".
function geoForEvent(ev) {
  const t = `${ev.headline || ""} ${ev.summary || ""}`.toLowerCase();
  if (/(\bny\b|new york)/.test(t)) return REGION_GEO["US"];
  if (/(\btx\b|texas)/.test(t))    return REGION_GEO["US"];
  if (/(\bca\b|california)/.test(t)) return REGION_GEO["US"];
  if (/(\bfl\b|florida)/.test(t))  return REGION_GEO["US"];
  if (/(\bnj\b|new jersey)/.test(t)) return REGION_GEO["US"];
  if (/(united kingdom|britain|uk\b)/.test(t)) return REGION_GEO["UK"];
  if (/(africa|nigeria|kenya|ghana|south africa)/.test(t)) return REGION_GEO["Africa"];
  if (/(united states|\bus\b|\busa\b|federal|hhs|cms|cdc|nih|hrsa|usda|va\b|sam\.gov|grants\.gov)/.test(t)) return REGION_GEO["US"];
  return REGION_GEO["Global"];
}

function useFetch(path, defaultValue, { adminGated = false } = {}) {
  const [data, setData] = useState(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // For AdminOne-gated endpoints, attach the AdminOne JWT when present;
        // for anonymous users, skip the call entirely (no console 401 noise).
        const headers = {};
        if (adminGated) {
          let token = null;
          try { token = window.localStorage.getItem("carelogix_adminone_token"); } catch { /* ignore */ }
          if (!token) {
            if (alive) { setData(null); setLoading(false); }
            return;
          }
          headers["Authorization"] = `Bearer ${token}`;
        }
        const r = await fetch(`${BACKEND}${path}`, { headers });
        if (!alive) return;
        if (!r.ok) {
          // Silence expected 401/403 for AdminOne-gated reads when unauthorised.
          if (adminGated && (r.status === 401 || r.status === 403)) {
            setData(null);
            setLoading(false);
            return;
          }
          throw new Error(`HTTP ${r.status}`);
        }
        const j = await r.json();
        if (!alive) return;
        setData(j);
      } catch (e) {
        if (alive) setError(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [path, adminGated]);
  return { data, loading, error };
}

export default function IntelligenceGlobe3D() {
  const services      = useFetch("/api/oracle/services/registry", null);
  const foundations   = useFetch("/api/oracle/funding/crm/foundations", null);
  const jurisdictions = useFetch("/api/oracle/jurisdiction/policies", null);
  const audit         = useFetch("/api/adminone/enterprise-audit/summary", null, { adminGated: true });
  const compliance    = useFetch("/api/adminone/governance/oracle-compliance", null, { adminGated: true });
  const contact       = useFetch("/api/oracle/funding/crm/contact", null);
  // Phase 35 — World Mesh events.
  const events        = useFetch("/api/oracle/world-mesh/events?limit=80", null);

  const [enabledLayers, setEnabledLayers] = useState({
    services: true,
    foundations: true,
    jurisdictions: true,
    audit: true,
    events: true,
  });
  const [dims, setDims] = useState({ w: 900, h: 540 });

  useEffect(() => {
    function onResize() {
      const w = Math.max(640, Math.min(1280, window.innerWidth - 80));
      setDims({ w, h: Math.round(w * 0.55) });
    }
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const toggle = (key) => setEnabledLayers((s) => ({ ...s, [key]: !s[key] }));

  // Aggregate per-region counts truthfully.
  const layerCounts = useMemo(() => {
    const acc = {
      services:      {},
      foundations:   {},
      jurisdictions: {},
      audit:         {},
    };
    (services.data?.services || []).forEach(() => {
      acc.services["Global"] = (acc.services["Global"] || 0) + 1;
    });
    (foundations.data?.foundations || []).forEach((f) => {
      const region = f.region || "Global";
      acc.foundations[region] = (acc.foundations[region] || 0) + 1;
    });
    (jurisdictions.data?.policies || []).forEach((p) => {
      const region = p.scope || "Global";
      acc.jurisdictions[region] = (acc.jurisdictions[region] || 0) + 1;
    });
    if (audit.data?.total_events != null) {
      acc.audit["Global"] = audit.data.total_events || 0;
    }
    return acc;
  }, [services.data, foundations.data, jurisdictions.data, audit.data]);

  const layerHasData = (k) => {
    if (k === "events") return (events.data?.events || []).length > 0;
    return Object.values(layerCounts[k] || {}).some((v) => v > 0);
  };

  // Convert counts into globe point objects.
  const pointData = useMemo(() => {
    const points = [];
    LAYER_DEFS.forEach((L) => {
      if (L.key === "events") return; // events render below (per-pin)
      if (!enabledLayers[L.key]) return;
      const counts = layerCounts[L.key] || {};
      Object.entries(counts).forEach(([region, count]) => {
        const geo = REGION_GEO[region];
        if (!geo || !count) return;
        points.push({
          lat: geo.lat,
          lng: geo.lng,
          size: Math.min(0.9, 0.18 + Math.sqrt(count) * 0.08),
          color: L.color,
          layer: L.label,
          region: geo.label,
          count,
        });
      });
    });
    // Phase 35 — render individual event pins, color-coded by event_type.
    if (enabledLayers.events) {
      const eventRows = events.data?.events || [];
      // Group by region+type to slightly nudge altitude when stacked.
      const stack = {};
      eventRows.forEach((ev) => {
        const geo = geoForEvent(ev);
        if (!geo) return;
        const key = `${geo.lat.toFixed(1)}_${geo.lng.toFixed(1)}_${ev.event_type}`;
        stack[key] = (stack[key] || 0) + 1;
        const idx = stack[key] - 1;
        const band = (ev.priority || {}).band || "LOW";
        const size = band === "CRITICAL" ? 0.65 : band === "HIGH" ? 0.50 : band === "MEDIUM" ? 0.35 : 0.22;
        points.push({
          lat: geo.lat + (idx * 0.15),
          lng: geo.lng + (idx * 0.15),
          size,
          color: EVENT_COLOR[ev.event_type] || "#a78bfa",
          layer: "World Mesh Event",
          region: `${ev.event_type} · ${band}`,
          count: 1,
          headline: ev.headline,
          verified: !!ev.source_verified,
        });
      });
    }
    return points;
  }, [layerCounts, enabledLayers, events.data]);

  // Arcs connect all coverage points to a Global hub for a sense of network.
  const arcData = useMemo(() => {
    const arcs = [];
    const hub = REGION_GEO["Global"];
    pointData.forEach((p) => {
      if (Math.abs(p.lat - hub.lat) < 1e-3 && Math.abs(p.lng - hub.lng) < 1e-3) return;
      arcs.push({
        startLat: hub.lat, startLng: hub.lng,
        endLat:   p.lat,   endLng:   p.lng,
        color: p.color,
      });
    });
    return arcs;
  }, [pointData]);

  return (
    <main style={styles.page} data-testid="intelligence-globe-3d-page">
      <header style={styles.header}>
        <div>
          <div style={styles.eyebrow}>PHASE 36 · GLOBAL INTELLIGENCE GLOBE™ · 3D</div>
          <h1 style={styles.title}>Constitutional Intelligence — World View</h1>
          <p style={styles.sub}>
            Interactive 3D visualization on top of real CareLogix™ ORACLE telemetry.
            Empty layers display{" "}
            <code style={styles.code}>NO_DATA_AVAILABLE</code>. No simulated
            opportunities, no fabricated regions.
          </p>
        </div>
        <div style={styles.headerLinks}>
          {(() => { try { return !!window.localStorage.getItem("carelogix_adminone_token"); } catch { return false; } })() && (
            <Link to="/adminone/telemetry" style={styles.link} data-testid="link-telemetry">AdminOne Telemetry →</Link>
          )}
          <Link to="/oracle/funding"     style={styles.link} data-testid="link-funding">Funding Command →</Link>
          <Link to="/intelligence-globe?view=2d" style={styles.link} data-testid="link-2d-globe">Switch to 2D →</Link>
        </div>
      </header>

      <section style={styles.kpiRow} data-testid="globe-kpi-row">
        <KPI label="Compliance Status"  value={compliance.data?.compliance_status || "—"}   sub="ORACLE" />
        <KPI label="Compliance Rate"    value={`${compliance.data?.compliance_rate ?? 0}%`} sub="@ current version" />
        <KPI label="Current Version"    value={compliance.data?.current_version || "—"}    sub="ORACLE version" />
        <KPI label="Audit Events · 24h" value={audit.data?.total_events ?? 0}              sub="across all domains" />
      </section>

      <section style={styles.controls} data-testid="globe-layer-controls">
        {LAYER_DEFS.map((l) => (
          <button
            key={l.key}
            type="button"
            onClick={() => toggle(l.key)}
            style={{
              ...styles.toggle,
              opacity: enabledLayers[l.key] ? 1 : 0.45,
              borderColor: l.color,
              color: enabledLayers[l.key] ? l.color : "#94a3b8",
            }}
            data-testid={`globe-toggle-${l.key}`}
          >
            {enabledLayers[l.key] ? "● " : "○ "}{l.label}{" "}
            {!layerHasData(l.key) && <span style={styles.noData}>NO_DATA_AVAILABLE</span>}
          </button>
        ))}
      </section>

      <section style={styles.globeWrap} data-testid="globe-3d-wrap">
        <Suspense fallback={<div style={styles.fallback}>Loading 3D globe…</div>}>
          <Globe
            width={dims.w}
            height={dims.h}
            backgroundColor="rgba(0,0,0,0)"
            globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
            bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
            atmosphereColor="#c5a059"
            atmosphereAltitude={0.18}
            pointsData={pointData}
            pointLat="lat"
            pointLng="lng"
            pointAltitude="size"
            pointColor="color"
            pointRadius={0.6}
            pointLabel={(d) => `
              <div style="font-family:Figtree,sans-serif;background:#0b0f14;color:#dfe3eb;padding:6px 10px;border:1px solid #c5a059;border-radius:8px;font-size:11px;line-height:1.5;max-width:280px">
                <div style="color:#c5a059;letter-spacing:.1em;font-size:9px">${d.layer.toUpperCase()}</div>
                <div><b>${d.region}</b></div>
                ${d.headline ? `<div style="margin-top:3px;color:#cbd5e1">${d.headline.slice(0,120)}</div>` : ""}
                ${d.verified !== undefined ? `<div style="margin-top:3px;color:${d.verified ? '#7AC860' : '#94a3b8'}">${d.verified ? '✓ source verified' : 'pending verification'}</div>` : `<div style="color:#94a3b8">count: ${d.count}</div>`}
              </div>`}
            arcsData={arcData}
            arcStartLat="startLat" arcStartLng="startLng"
            arcEndLat="endLat"     arcEndLng="endLng"
            arcColor="color"
            arcAltitudeAutoScale={0.4}
            arcStroke={0.4}
            arcDashLength={0.4}
            arcDashGap={0.2}
            arcDashAnimateTime={2400}
          />
        </Suspense>
      </section>

      <section style={styles.legend} data-testid="globe-legend">
        {LAYER_DEFS.map((l) => (
          <span key={l.key} style={styles.legendChip}>
            <span style={{ ...styles.legendDot, background: l.color }} />
            {l.label}
          </span>
        ))}
      </section>

      <section style={styles.panel} data-testid="globe-contact-panel">
        <header style={styles.panelHead}>
          <h2 style={styles.panelTitle}>Capital & Funding Command — Contact</h2>
          <span style={styles.panelMeta}>Advisory only · no guarantees</span>
        </header>
        {contact.data?.primary_contact ? (
          <div style={styles.contactGrid}>
            <KV k="Label"  v={contact.data.primary_contact.label} />
            <KV k="Email"  v={contact.data.primary_contact.email} />
            <KV k="Phone"  v={contact.data.primary_contact.phone} />
            <KV k="Notice" v={contact.data.constitutional_note} />
          </div>
        ) : (
          <div style={styles.sub}>NO_DATA_AVAILABLE</div>
        )}
      </section>

      <footer style={styles.footer}>
        CareLogix™ ORACLE · 3D Intelligence Globe · English-canonical ·
        Earth textures © three-globe authors · Data: /api/oracle/services/registry,
        /api/oracle/funding/crm/foundations, /api/oracle/jurisdiction/policies,
        /api/adminone/enterprise-audit/summary, /api/adminone/governance/oracle-compliance.
      </footer>
    </main>
  );
}

function KPI({ label, value, sub }) {
  return (
    <div style={styles.kpi} data-testid={`globe-kpi-${String(label).toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
      <div style={styles.kpiLbl}>{label}</div>
      <div style={styles.kpiVal}>{value}</div>
      <div style={styles.kpiSub}>{sub}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={styles.kvRow}>
      <span style={styles.kvK}>{k}</span>
      <span style={styles.kvV}>{v}</span>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "radial-gradient(ellipse at top, #0e1726 0%, #06090f 60%, #03060a 100%)",
    color: "#dfe3eb",
    padding: "32px 24px 60px",
    fontFamily: "'Figtree', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24, marginBottom: 18, flexWrap: "wrap" },
  eyebrow: { color: "#c5a059", letterSpacing: "0.2em", fontSize: 10.5, fontFamily: "'Space Mono', monospace" },
  title:   { fontSize: 28, margin: "6px 0 4px", color: "#f5e9c8", letterSpacing: "0.01em" },
  sub:     { fontSize: 13, color: "#94a3b8", maxWidth: 760, lineHeight: 1.55 },
  code:    { padding: "1px 6px", borderRadius: 4, background: "rgba(197,160,89,0.12)", color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  headerLinks: { display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" },
  link: { color: "#c5a059", textDecoration: "none", fontSize: 12, letterSpacing: "0.05em" },

  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 18 },
  kpi:    { padding: "12px 14px", border: "1px solid #1f2937", borderRadius: 10, background: "rgba(255,255,255,0.02)" },
  kpiLbl: { fontSize: 10.5, color: "#94a3b8", letterSpacing: "0.16em", textTransform: "uppercase" },
  kpiVal: { fontSize: 22, color: "#f5e9c8", marginTop: 4, fontWeight: 600 },
  kpiSub: { fontSize: 11, color: "#6b7280", marginTop: 2 },

  controls: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 },
  toggle:   { padding: "6px 10px", borderRadius: 999, border: "1px solid #c5a059", background: "transparent", fontSize: 11.5, cursor: "pointer", letterSpacing: "0.05em", display: "inline-flex", alignItems: "center", gap: 6 },
  noData:   { color: "#E5484D", fontFamily: "'Space Mono', monospace", fontSize: 9.5 },

  globeWrap: {
    border: "1px solid #1f2937",
    borderRadius: 16,
    overflow: "hidden",
    marginBottom: 12,
    background: "radial-gradient(ellipse at center, #0b1018 0%, #03060a 80%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 540,
    padding: 12,
  },
  fallback: { color: "#94a3b8", fontSize: 13, padding: "120px 0" },

  legend: { display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 18, fontSize: 12, color: "#94a3b8" },
  legendChip: { display: "inline-flex", alignItems: "center", gap: 6 },
  legendDot:  { display: "inline-block", width: 10, height: 10, borderRadius: "50%" },

  panel: { border: "1px solid #1f2937", borderRadius: 12, padding: "16px 18px", marginBottom: 18, background: "rgba(255,255,255,0.02)" },
  panelHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  panelTitle:{ fontSize: 14, color: "#f5e9c8", margin: 0 },
  panelMeta: { fontSize: 11, color: "#94a3b8" },
  contactGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 },
  kvRow: { display: "flex", flexDirection: "column", gap: 2, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  kvK:   { fontSize: 10.5, color: "#94a3b8", letterSpacing: "0.12em", textTransform: "uppercase" },
  kvV:   { fontSize: 12.5, color: "#dfe3eb", lineHeight: 1.45 },

  footer: { fontSize: 10.5, color: "#6b7280", marginTop: 16, lineHeight: 1.6 },
};
